import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, X, Save, RefreshCw, GripVertical } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  title: '', tag: '', kicker: 'SYNERGY STACK', subtitle: '',
  itemsText: '', price: '', image: '', sortOrder: 100,
};

const inputCls = "w-full bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md px-3 py-2.5 text-[14px] text-neutral-900 dark:text-neutral-100 outline-none focus:border-neutral-400 dark:focus:border-neutral-600";

const Label = ({ children, required }) => (
  <label className="block text-[10.5px] tracking-[0.12em] text-neutral-500 uppercase mb-1.5">
    {children}{required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

const StackForm = ({ initial, onClose, onSave, saving }) => {
  const [form, setForm] = useState(initial);
  const [err, setErr] = useState('');
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = () => {
    if (!form.title.trim()) return setErr('Title is required');
    const price = parseFloat(form.price);
    if (Number.isNaN(price) || price < 0) return setErr('Valid price is required');
    const items = (form.itemsText || '').split('\n').map(s => s.trim()).filter(Boolean);
    const sortOrder = parseInt(form.sortOrder) || 100;
    onSave({
      title: form.title.trim(), tag: form.tag, kicker: form.kicker,
      subtitle: form.subtitle, items, price, image: form.image, sortOrder,
    });
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="relative bg-white dark:bg-[#141416] rounded-2xl w-full max-w-2xl my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 dark:border-neutral-800">
          <h3 className="font-serif text-[22px] text-neutral-900 dark:text-neutral-100">
            {initial.id ? 'Edit stack' : 'Add stack'}
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full border border-neutral-200 dark:border-neutral-700 flex items-center justify-center hover:bg-neutral-50 dark:hover:bg-neutral-800"><X size={15} /></button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <Label required>Title</Label>
            <input className={inputCls} value={form.title} onChange={(e) => upd('title', e.target.value)} placeholder="e.g. The Main Event" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Tag</Label>
              <input className={inputCls} value={form.tag} onChange={(e) => upd('tag', e.target.value)} placeholder="e.g. MEN'S PERFORMANCE" />
            </div>
            <div>
              <Label>Kicker</Label>
              <input className={inputCls} value={form.kicker} onChange={(e) => upd('kicker', e.target.value)} placeholder="e.g. SYNERGY STACK" />
            </div>
          </div>

          <div>
            <Label>Subtitle</Label>
            <input className={inputCls} value={form.subtitle} onChange={(e) => upd('subtitle', e.target.value)} placeholder="Marketing tagline for the stack" />
          </div>

          <div>
            <Label>Items (one per line)</Label>
            <textarea rows={4} className={inputCls} value={form.itemsText} onChange={(e) => upd('itemsText', e.target.value)} placeholder={'Tadalafil (Cialis) 5mg · 100 tabs\nSildenafil (Viagra) 100mg · 100 tabs'} />
            <p className="text-[11px] text-neutral-500 mt-1">One line per item. Use " · " between name and pack for consistent styling.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label required>Price (USD)</Label>
              <input type="number" step="0.01" min="0" className={inputCls} value={form.price} onChange={(e) => upd('price', e.target.value)} placeholder="e.g. 98.68" />
            </div>
            <div>
              <Label>Sort order</Label>
              <input type="number" className={inputCls} value={form.sortOrder} onChange={(e) => upd('sortOrder', e.target.value)} placeholder="Lower = shown first" />
            </div>
          </div>

          <div>
            <Label>Image URL</Label>
            <input className={inputCls} value={form.image} onChange={(e) => upd('image', e.target.value)} placeholder="https://…/stack-hero.webp" />
            {form.image && (
              <div className="mt-2 w-40 h-24 rounded-md border border-neutral-200 dark:border-neutral-800 bg-neutral-100 dark:bg-neutral-900 overflow-hidden">
                <img src={form.image} alt="preview" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = 0.2; }} />
              </div>
            )}
          </div>

          {err && <p className="text-red-600 text-[13px]">{err}</p>}
        </div>

        <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-800 flex justify-end gap-2">
          <button onClick={onClose} className="border border-neutral-200 dark:border-neutral-700 rounded-md px-4 py-2 text-[13.5px] hover:bg-neutral-50 dark:hover:bg-neutral-800">Cancel</button>
          <button onClick={submit} disabled={saving} className={`inline-flex items-center gap-1.5 rounded-md px-5 py-2 text-[13.5px] text-white ${saving ? 'bg-neutral-400 cursor-not-allowed' : 'bg-[#1e293b] hover:bg-[#0f172a]'}`}>
            <Save size={14} /> {saving ? 'Saving…' : (initial.id ? 'Save changes' : 'Create stack')}
          </button>
        </div>
      </div>
    </div>
  );
};

const StacksPanel = () => {
  const { password } = useAdminAuth();
  const headers = useMemo(() => ({ 'X-Admin-Password': password }), [password]);
  const [stacks, setStacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data } = await axios.get(`${API}/stacks`);
      setStacks(data || []);
    } catch { setErr('Failed to load stacks'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    setSaving(true);
    try {
      if (editing.id) {
        await axios.patch(`${API}/stacks/${editing.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/stacks`, payload, { headers });
      }
      setEditing(null);
      await load();
    } catch (e) {
      alert('Failed to save: ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete stack "${s.title}"?`)) return;
    try {
      await axios.delete(`${API}/stacks/${s.id}`, { headers });
      await load();
    } catch (e) { alert('Failed to delete: ' + (e?.response?.data?.detail || e.message)); }
  };

  const openEdit = (s) => setEditing({
    ...s,
    price: String(s.price ?? ''),
    itemsText: (s.items || []).join('\n'),
    sortOrder: s.sortOrder ?? 100,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-[22px] text-neutral-900 dark:text-neutral-100">Synergy Stacks</h2>
          <p className="text-[12.5px] text-neutral-500">Featured product bundles shown in the hero carousel on the homepage.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-2 text-[13px] hover:bg-neutral-50 dark:hover:bg-neutral-800"><RefreshCw size={14} /> Refresh</button>
          <button onClick={() => setEditing({ ...emptyForm })} className="inline-flex items-center gap-1.5 bg-[#1e293b] hover:bg-[#0f172a] text-white rounded-md px-4 py-2 text-[13.5px]"><Plus size={15} /> Add stack</button>
        </div>
      </div>

      {err && <div className="text-red-600 text-[13px]">{err}</div>}
      {loading && stacks.length === 0 && <div className="py-16 text-center text-neutral-500 text-[13.5px]">Loading stacks…</div>}
      {!loading && stacks.length === 0 && <div className="py-16 text-center text-neutral-500 text-[13.5px]">No stacks yet. Click "Add stack" to create one.</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {stacks.map(s => (
          <div key={s.id} className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] overflow-hidden flex flex-col">
            <div className="aspect-[16/9] bg-neutral-100 dark:bg-neutral-900 relative">
              {s.image ? (
                <img src={s.image} alt={s.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = 0.15; }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-neutral-400 text-[13px]">No image</div>
              )}
              <span className="absolute top-2 left-2 bg-black/60 text-white text-[10px] tracking-widest uppercase px-2 py-1 rounded">#{s.sortOrder ?? '—'} · {s.tag || 'STACK'}</span>
            </div>
            <div className="p-4 flex flex-col flex-1">
              <div className="text-[10.5px] tracking-[0.15em] uppercase text-neutral-500">{s.kicker || 'SYNERGY STACK'}</div>
              <h3 className="font-serif text-[20px] text-neutral-900 dark:text-neutral-100 mt-1">{s.title}</h3>
              {s.subtitle && <p className="text-[12.5px] text-neutral-500 mt-1 line-clamp-2">{s.subtitle}</p>}
              <ul className="mt-2 space-y-1">
                {(s.items || []).slice(0, 4).map((it, idx) => (
                  <li key={idx} className="text-[12px] text-neutral-600 dark:text-neutral-300 truncate">• {it}</li>
                ))}
              </ul>
              <div className="mt-auto pt-3 flex items-center justify-between">
                <span className="text-[18px] font-semibold text-neutral-900 dark:text-neutral-100">${Number(s.price || 0).toFixed(2)}</span>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(s)} className="inline-flex items-center gap-1 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-1.5 text-[12.5px] hover:bg-neutral-50 dark:hover:bg-neutral-800"><Pencil size={13} /> Edit</button>
                  <button onClick={() => del(s)} className="p-1.5 rounded-md text-neutral-400 hover:text-red-500" title="Delete"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <StackForm initial={editing} onClose={() => setEditing(null)} onSave={save} saving={saving} />
      )}
    </div>
  );
};

export default StacksPanel;
